/**
 * Determines and opens the appropriate "Mark as Completed" modal
 * (happy-flow or comment-RPA) based on current order state.
 *
 * Logs the mark-as-completed event and the resulting modal-open event
 * to BigQuery before dispatching the modal via Redux.
 */
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import { setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../redux/store/store";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";
import {
  logCommentRpaModalOpened,
  logHappyFlowModalOpened,
  logMarkAsCompletedButtonClicked,
} from "./singleOrderLayoutEventLogger";

/**
 * Parameters for {@link openCorrectModal}.
 *
 * All order data comes from the Postgres-backed data-accumulator context;
 * the legacy `singleMedicalPaOrder` and `getJCodes` params have been removed.
 */
interface OpenCorrectModalParams {
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  coverageDetails: SingleCoverage[] | null | undefined;
  authOnFileDetails: AuthOnFile | null | undefined;
  orgId: string | null | undefined;
  userId: string | undefined;
  userEmail: string | undefined;
  payerName: string;
  shouldOpenHappyModalValue: boolean;
  dispatch: AppDispatch;
}

/**
 * Opens the correct modal based on the order state.
 * Logs the mark as completed button click and opens either the happy flow modal
 * or the comment RPA modal based on shouldOpenHappyModalValue.
 */
export const openCorrectModal = ({
  generalOrderDetails,
  coverageDetails,
  authOnFileDetails,
  orgId,
  userId,
  userEmail,
  payerName,
  shouldOpenHappyModalValue,
  dispatch,
}: OpenCorrectModalParams) => {
  const planName =
    coverageDetails?.find((coverage) => coverage.coverage_type === "primary")
      ?.plan_name ?? "";
  const jCodes =
    getAllJCodesFromAuthOnFile(authOnFileDetails ?? undefined) ?? [];

  logMarkAsCompletedButtonClicked({
    orgId,
    userId,
    userEmail,
    payerName,
    masterAuthStatusOld: generalOrderDetails?.status?.master_auth_status,
    masterAuthStatusNew: authOnFileDetails?.auth_status,
    planName,
    jCodes: jCodes.map((jCode) => jCode?.j_code ?? "").filter(Boolean),
    order_id: generalOrderDetails?.order_id ?? "",
    patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
  });

  if (shouldOpenHappyModalValue) {
    dispatch(setOpenedModalId({ id: ModalId.HAPPY_FLOW_MODAL }));
    logHappyFlowModalOpened({
      orgId,
      userId,
      userEmail,
      payerName,
      order_id: generalOrderDetails?.order_id ?? "",
      patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
    });
  } else {
    dispatch(setOpenedModalId({ id: ModalId.COMMENT_RPA_MODAL_NEW }));
    logCommentRpaModalOpened({
      orgId,
      userId,
      userEmail,
      payerName,
      order_id: generalOrderDetails?.order_id ?? "",
      patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
    });
  }
};
